import { toRecruitmentTypeFormValue } from '@inquiries/-lib/recruitment-type-options'
import { useMemo } from 'react'
import { splitQuery } from '@/lib/search-utils'
import type { InquirySearchToken } from '../types'

/**
 * 検索クエリをトークンに分解するカスタムフック
 *
 * 検索文字列を解析し、主管部署、ステータスなどの構造化されたトークンに変換します
 */
export function useInquirySearchTokens(value: string, divisionMap: Map<number, string>): InquirySearchToken[] {
  return useMemo<InquirySearchToken[]>(() => {
    if (!value) return []

    const parts = splitQuery(value)
    const result: InquirySearchToken[] = []

    for (const part of parts) {
      // 自分の部署
      if (part === 'my_division:true') {
        result.push({ type: 'my_division', raw: part })
        continue
      }

      // 自身の担当
      if (part === 'my_assignment:true') {
        result.push({ type: 'my_assignment', raw: part })
        continue
      }

      // 商談未紐付け
      if (part === 'no_deal:true') {
        result.push({ type: 'no_deal', raw: part })
        continue
      }

      // 主管部署
      if (part.startsWith('primary_division:')) {
        const id = Number.parseInt(part.split(':')[1] || '', 10)
        if (!Number.isNaN(id)) {
          result.push({
            type: 'primary_division',
            id,
            name: divisionMap.get(id) || `ID:${id}`,
            raw: part,
          })
        }
        continue
      }

      // 営業担当
      if (part.startsWith('sales_member:')) {
        const name = part.split(':')[1]
        if (name) {
          result.push({ type: 'sales_member', name, raw: part })
        }
        continue
      }

      // サービスマネージャ
      if (part.startsWith('service_manager:')) {
        const name = part.split(':')[1]
        if (name) {
          result.push({ type: 'service_manager', name, raw: part })
        }
        continue
      }

      // ステータス
      if (part.startsWith('inquiry_status:')) {
        const name = part.split(':')[1]
        if (name) {
          result.push({ type: 'inquiry_status', name, raw: part })
        }
        continue
      }

      // 作業場所
      if (part.startsWith('work_location:')) {
        const value = part.split(':')[1]
        if (value === 'onsite' || value === 'offsite' || value === 'remote') {
          result.push({ type: 'work_location', value, raw: part })
        }
        continue
      }

      // 募集形態
      if (part.startsWith('recruitment_type:')) {
        const value = toRecruitmentTypeFormValue(part.split(':')[1] ?? '')
        if (value) {
          result.push({ type: 'recruitment_type', value, raw: part })
        }
        continue
      }

      // 開始月
      if (part.startsWith('start_month:')) {
        const value = part.split(':')[1]
        if (value) {
          result.push({ type: 'start_month', value, raw: part })
        }
        continue
      }

      // なる早
      if (part === 'start_asap:true') {
        result.push({ type: 'start_asap', raw: part })
        continue
      }

      // 金額下限
      if (part.startsWith('amount_min:')) {
        const value = part.split(':')[1]
        if (value) {
          result.push({ type: 'amount_min', value, raw: part })
        }
        continue
      }

      // 金額上限
      if (part.startsWith('amount_max:')) {
        const value = part.split(':')[1]
        if (value) {
          result.push({ type: 'amount_max', value, raw: part })
        }
        continue
      }

      // 契約種別
      if (part.startsWith('contract_type:')) {
        const value = part.split(':')[1]
        if (value) {
          result.push({ type: 'contract_type', value, raw: part })
        }
        continue
      }

      // アサインステータス
      if (part.startsWith('assign_status:')) {
        const value = part.split(':')[1]
        if (value) {
          result.push({ type: 'assign_status', value, raw: part })
        }
        continue
      }

      // アサイン開始月
      if (part.startsWith('assign_start_month:')) {
        const value = part.split(':')[1]
        if (value) {
          result.push({ type: 'assign_start_month', value, raw: part })
        }
        continue
      }

      // 除外キーワード
      if (part.startsWith('-')) {
        const keyword = part.substring(1).replace(/^["']|["']$/g, '')
        if (keyword) {
          result.push({ type: 'exclude', value: keyword, raw: part })
        }
        continue
      }

      // 通常のキーワード
      const keyword = part.replace(/^["']|["']$/g, '')
      if (keyword) {
        result.push({ type: 'query', value: keyword, raw: part })
      }
    }

    return result
  }, [value, divisionMap])
}
